import { useEffect, useState, ReactNode, useCallback, useRef, useMemo } from "react";
import {
    User,
    onAuthStateChanged,
    signOut,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    GoogleAuthProvider,
    GithubAuthProvider,
    signInWithPopup,
    sendSignInLinkToEmail,
    isSignInWithEmailLink,
    signInWithEmailLink,
    getAdditionalUserInfo
} from "firebase/auth";
import { auth as firebaseAuth, db } from "../firebase/app";
import { doc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import {
    useClearWorkspaceState,
    useClearWorkflowState,
    useClearRapidDataState,
    useCurrentWorkspace,
    useSetUser
} from "@/store";
import { AuthContext, LoginSuccessCallback } from "./AuthContext";
import eventTracker from "@/analytics/EventTracker";
import { AnalyticsMethod } from "@/analytics/types";

// Get the appropriate action code settings based on current environment
const getActionCodeSettings = () => {
    // Extract just the origin (protocol + hostname + port)
    const origin = window.location.origin;

    return {
        // Use the exact origin from the current browser window
        url: `${origin}/login`,
        // This must be true for email link sign-in
        handleCodeInApp: true
    };
};

/**
 * Gets UTM data from cookie if available.
 * The utm.js script (loaded in index.html) stores UTM params in a cookie called "cookie_utms".
 */
function getUtmFromCookie(): Record<string, string> | null {
    try {
        const cookies = document.cookie.split(";");
        for (const cookie of cookies) {
            const [name, value] = cookie.trim().split("=");
            if (name === "cookie_utms" && value) {
                return JSON.parse(decodeURIComponent(value));
            }
        }
    } catch (e) {
        // Fail silently if cookie parsing fails
    }
    return null;
}

/**
 * Records a new user signup event.
 * This mirrors the recordUserSignup function in app/src/scripts/eventTracker.js
 */
async function recordUserSignup(newUserId: string) {
    const locationPathname = window.location.pathname;
    const slugParts = locationPathname.split("/").splice(1);
    const sourceInfo = { site: "rapid", slug: slugParts };

    // Get UTM data from cookie (set by utm.js script)
    const utm = getUtmFromCookie();

    // Update user doc with source and UTM info
    const userDocRef = doc(db, "users", newUserId);
    const userUpdate: { source: typeof sourceInfo; utm?: typeof utm } = { source: sourceInfo };
    if (utm) {
        userUpdate.utm = utm;
    }
    await setDoc(userDocRef, userUpdate, { merge: true });

    // Identify the user in analytics (attaching previous anonymous events to this user)
    await eventTracker.trackEvent({
        method: AnalyticsMethod.IDENTIFY,
        id: newUserId,
        identity: { id: newUserId, workspaceId: newUserId } as any
    });

    // Record "User Signed Up" event (client-side via Segment)
    await eventTracker.trackEvent({
        method: AnalyticsMethod.TRACK,
        id: "User Signed Up",
        data: { signup_location: "rapid", workspaceId: newUserId }
    });

    // Record "User Signed Up Browser" event (server-side via /query/event)
    await eventTracker.trackEvent({
        method: AnalyticsMethod.TRACK,
        id: "User Signed Up Browser",
        data: { signup_location: "rapid", workspaceId: newUserId }
    });
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const api = useApi();
    const currentWorkspace = useCurrentWorkspace();
    const clearWorkspaceState = useClearWorkspaceState();
    const clearWorkflowState = useClearWorkflowState();
    const clearRapidDataState = useClearRapidDataState();
    const setUserInStore = useSetUser();

    // Due to the usage of navigate within useEffect, we don't want infinite loop, so we use a ref to prevent rerenders
    // TODO(japrescott, 2025-08-20): Refactor to safeguard navigate better
    const navigate = useNavigate();
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;

    const [user, setUser] = useState<User | null>(null);
    const [claims, setClaims] = useState<Record<string, any> | null>(null);
    const [apiAuthenticated, setApiAuthenticated] = useState<boolean>(false);

    const [loading, setLoading] = useState(true);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [emailLinkSent, setEmailLinkSent] = useState(false);
    const [emailForSignIn, setEmailForSignIn] = useState<string | null>(
        localStorage.getItem("emailForSignIn")
    );
    const [pendingAuthInfo, setPendingAuthInfo] = useState(false);

    // Login modal state
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const modalOnSuccessCallbackRef = useRef<LoginSuccessCallback | undefined>(undefined);

    // Guest auth state
    const [guestUid, setGuestUid] = useState<string | null>(null);
    const [guestAuthInitialized, setGuestAuthInitialized] = useState(false);

    // Workspace persistence is now handled by Zustand store automatically

    const resetSessionAndClaims = useCallback(async () => {
        await api.auth.logout();
        setClaims(null);
        setApiAuthenticated(false);
        // Clear workspace state from store
        clearWorkspaceState();
        // Clear workflow state from store
        clearWorkflowState();
        // Clear rapid data state from store
        clearRapidDataState();
    }, [api, clearWorkspaceState, clearWorkflowState, clearRapidDataState]);

    const fetchSessionAndClaims = useCallback(async () => {
        if (!user) {
            setClaims(null);
            setApiAuthenticated(false);
            return;
        }

        try {
            const idTokenResult = await user.getIdTokenResult(true);
            setClaims(idTokenResult.claims);
            await api.auth.createSession(idTokenResult.token, user.uid);
            setApiAuthenticated(true);
        } catch (error) {
            console.error("Error fetching session and claims:", error);
            setClaims(null);
            setApiAuthenticated(false);
        }
    }, [user, api]);

    const activeSession = useMemo(() => {
        if (user && apiAuthenticated) return true;
        return false;
    }, [user, apiAuthenticated]);

    const authenticatedWorkspace = useMemo(() => {
        // Require active session, valid workspace, and workspace to be fully ready
        if (activeSession && currentWorkspace?.id) {
            return true;
        }
        return false;
    }, [activeSession, currentWorkspace]);

    // Effect to run when user changes - but only after initial load
    useEffect(() => {
        // Don't do anything during initial load to prevent clearing valid sessions
        if (!initialLoadComplete) {
            return;
        }

        // Check if we're in an email link flow to prevent premature redirects
        const isEmailLinkFlow =
            window.location.search.includes("mode=signIn") &&
            window.location.search.includes("oobCode=");

        if (user && !pendingAuthInfo) {
            fetchSessionAndClaims();
        } else if (!user) {
            // Don't redirect during email link flow - let LoginPage handle it
            if (isEmailLinkFlow) {
                // Still clean up session but don't redirect
                resetSessionAndClaims().then(() => {
                    setLoading(false); // Ensure loading is reset when user is null
                    // No navigation during email link flow
                });
            } else {
                resetSessionAndClaims().then(() => {
                    setLoading(false); // Ensure loading is reset when user is null

                    // Handle navigation from workspace-namespaced routes to non-namespaced routes
                    const currentPath = window.location.pathname;
                    const pathSegments = currentPath.split("/").filter(Boolean);

                    // Don't redirect public share routes (/p/*)
                    if (currentPath.startsWith("/p/")) {
                        // Public share routes don't need authentication, so don't redirect
                        return;
                    }

                    if (pathSegments.length > 1) {
                        // If we're on a workspace-namespaced route like /workspace-a/some-page
                        // Navigate to the non-namespaced version: /some-page
                        const nonNamespacedPath = "/" + pathSegments.slice(1).join("/");
                        navigateRef.current(nonNamespacedPath, { replace: true });
                    } else {
                        // If we're on workspace root like /workspace-a or already on /
                        // Navigate to global root
                        navigateRef.current("/", { replace: true });
                    }
                });
            }
        }
    }, [user, initialLoadComplete, pendingAuthInfo, fetchSessionAndClaims, resetSessionAndClaims]);

    // Track if we should disable cookie exchange (e.g., during logout)
    const [cookieExchangeDisabled, setCookieExchangeDisabled] = useState(false);

    // Exchange session cookie for Firebase auth on initial load
    const exchangeCookie = useCallback(async () => {
        // Don't exchange cookie if disabled (e.g., during logout)
        if (cookieExchangeDisabled) {
            return false;
        }

        try {
            const response = await api.auth.getCoreAppCustomAuthToken();
            if (response?.jwt) {
                const { signInWithCustomToken } = await import("firebase/auth");
                await signInWithCustomToken(firebaseAuth, response.jwt);
                return true;
            }
        } catch (error) {
            // Silent fail - no session cookie available
            console.debug("No session cookie available for exchange:", error);
        }
        return false;
    }, [api, cookieExchangeDisabled]);

    useEffect(() => {
        let hasExchangedCookie = false;

        const unsub = onAuthStateChanged(firebaseAuth, async (u) => {
            // If user is authenticated, disable cookie exchange to prevent issues during logout
            if (u) {
                setCookieExchangeDisabled(true);
            }

            // On initial load, try to exchange session cookie before setting user state
            if (!initialLoadComplete && !u && !hasExchangedCookie) {
                hasExchangedCookie = true;
                const exchanged = await exchangeCookie();
                if (exchanged) {
                    // If exchange was successful, Firebase auth will trigger this listener again
                    // with the authenticated user, so we return early here
                    return;
                }
            }

            setUser(u);
            setLoading(false); // Always reset loading on auth state change

            // Mark initial load as complete after first auth state change
            if (!initialLoadComplete) {
                setInitialLoadComplete(true);
            }

            if (u) {
                // Check if user is missing required auth info (like displayName)
                const missingDisplayName = !u.displayName;
                setPendingAuthInfo(missingDisplayName);
            } else {
                // Reset pending auth info when user logs out
                setPendingAuthInfo(false);
                // Force reset loading state when user is null (logged out)
                setLoading(false);
                // Re-enable cookie exchange for future visits
                setCookieExchangeDisabled(false);
            }
        });
        return unsub;
    }, [initialLoadComplete, exchangeCookie]);

    // Sync user to Zustand store for components that need it (e.g., UpgradeModal)
    useEffect(() => {
        if (user) {
            setUserInStore({ uid: user.uid, email: user.email, displayName: user.displayName });
        } else {
            setUserInStore(null);
        }
    }, [user, setUserInStore]);

    useEffect(() => {
        if (
            isLoginModalOpen !== null &&
            authenticatedWorkspace &&
            modalOnSuccessCallbackRef.current
        ) {
            modalOnSuccessCallbackRef.current?.(user!, currentWorkspace!);
            modalOnSuccessCallbackRef.current = undefined;
        }
    }, [authenticatedWorkspace, currentWorkspace, isLoginModalOpen, user]);

    // Store emailForSignIn in localStorage when it changes
    useEffect(() => {
        if (emailForSignIn) {
            localStorage.setItem("emailForSignIn", emailForSignIn);
        } else {
            localStorage.removeItem("emailForSignIn");
        }
    }, [emailForSignIn]);

    const handleAuthError = (error: any): null => {
        console.error("Authentication error:", error);

        if (error?.code === "auth/email-already-in-use") {
            setError("This email address is already in use. Try logging in instead.");
        } else if (error?.code === "auth/wrong-password" || error?.code === "auth/user-not-found") {
            setError("Invalid email or password. Please try again.");
        } else if (error?.code === "auth/weak-password") {
            setError("Password is too weak. Please use a stronger password.");
        } else if (error?.message?.includes("Unverified email")) {
            setError(
                "This account requires email verification. We recommend using the magic link option below or social login."
            );
        } else if (error?.code === "auth/invalid-action-code") {
            setError(
                "The sign-in link has expired or has already been used. Please request a new link."
            );
        } else if (error?.code === "auth/invalid-dynamic-link-domain") {
            setError(
                "Cannot send email link at this time. Please try a social login method instead."
            );
        } else {
            setError(error?.message || "Authentication failed");
        }
        return null;
    };

    const loginWithEmail = async (email: string, password: string) => {
        setError(null);
        try {
            const result = await signInWithEmailAndPassword(firebaseAuth, email, password);
            return result.user;
        } catch (err) {
            return handleAuthError(err);
        }
    };

    const signUpWithEmail = async (email: string, password: string) => {
        setError(null);
        try {
            const result = await createUserWithEmailAndPassword(firebaseAuth, email, password);
            // createUserWithEmailAndPassword always creates a new user
            try {
                await recordUserSignup(result.user.uid);
            } catch (signupTrackingError) {
                console.error("Failed to record user signup:", signupTrackingError);
                // Don't block auth flow if tracking fails
            }
            return result.user;
        } catch (err) {
            return handleAuthError(err);
        }
    };

    const sendLoginLink = async (email: string): Promise<boolean> => {
        setError(null);
        setEmailLinkSent(false);

        try {
            // Get the action code settings for the current environment
            const actionCodeSettings = getActionCodeSettings();

            await sendSignInLinkToEmail(firebaseAuth, email, actionCodeSettings);

            // Save the email to complete the login later
            setEmailForSignIn(email);

            setEmailLinkSent(true);
            return true;
        } catch (err) {
            console.error("Failed to send login link:", err);
            handleAuthError(err);
            return false;
        }
    };

    const completeLoginWithEmailLink = async (): Promise<User | null> => {
        setError(null);

        // Validate the current URL
        const currentUrl = window.location.href;

        // Check if the current URL contains expected parameters
        if (!currentUrl.includes("mode=signIn") || !currentUrl.includes("oobCode=")) {
            console.error("URL doesn't contain expected sign-in parameters");
            setError("Invalid sign-in link format. Please request a new link.");
            return null;
        }

        // Check if we have an email for sign-in
        if (!emailForSignIn) {
            console.error("No email found for sign-in");
            setError(
                "Email is required to complete sign-in. Please try again or request a new link."
            );
            return null;
        }

        // Check if the current URL is a sign-in link
        if (!isSignInWithEmailLink(firebaseAuth, currentUrl)) {
            console.error("URL is not a valid sign-in link");
            setError("The link is not valid for sign-in. Please request a new link.");
            return null;
        }

        try {
            // Attempt to perform the sign-in
            const result = await signInWithEmailLink(firebaseAuth, emailForSignIn, currentUrl);

            // Check if this is a new user signup via email link
            const additionalUserInfo = getAdditionalUserInfo(result);
            if (additionalUserInfo?.isNewUser) {
                try {
                    await recordUserSignup(result.user.uid);
                } catch (signupTrackingError) {
                    console.error("Failed to record user signup:", signupTrackingError);
                    // Don't block auth flow if tracking fails
                }
            }

            // Clear email from storage after successful sign-in
            setEmailForSignIn(null);

            return result.user;
        } catch (err) {
            console.error("Failed to complete sign-in with email link:", err);
            return handleAuthError(err);
        }
    };

    const completeLoginWithEmailLinkAndName = async (name: string): Promise<User | null> => {
        // User should already be signed in but missing displayName
        if (!user) {
            setError("No authenticated user found. Please try the email link again.");
            return null;
        }

        try {
            // Set the display name on the current user
            const { updateProfile } = await import("firebase/auth");
            await updateProfile(user, { displayName: name });

            // Clear the pending auth info flag - this will trigger workspace loading
            setPendingAuthInfo(false);

            return user;
        } catch (err) {
            console.error("Failed to update user profile:", err);
            setError("Failed to update your profile. Please try again.");
            return null;
        }
    };

    const loginWithProvider = async (provider: GoogleAuthProvider | GithubAuthProvider) => {
        setError(null);
        try {
            const result = await signInWithPopup(firebaseAuth, provider);
            // Check if this is a new user signup via OAuth
            const additionalUserInfo = getAdditionalUserInfo(result);
            if (additionalUserInfo?.isNewUser) {
                try {
                    await recordUserSignup(result.user.uid);
                } catch (signupTrackingError) {
                    console.error("Failed to record user signup:", signupTrackingError);
                    // Don't block auth flow if tracking fails
                }
            }
            return result.user;
        } catch (err) {
            return handleAuthError(err);
        }
    };

    const loginWithGoogle = () => loginWithProvider(new GoogleAuthProvider());
    const loginWithGithub = () => loginWithProvider(new GithubAuthProvider());

    const logout = async () => {
        setError(null);
        setLoading(true); // Set loading during logout process
        try {
            // Firebase User logout - sets user to null
            // this will trigger the useEffect that resets
            // the session and claims and cleans up the workspace
            // and workflow state
            await signOut(firebaseAuth);
        } catch (err) {
            handleAuthError(err);
        } finally {
            // Force reset loading state after logout attempt
            setLoading(false);
        }
    };

    // Login modal functions
    const requireLogin = useCallback(
        (onSuccess?: LoginSuccessCallback) => {
            if (authenticatedWorkspace) {
                onSuccess?.(user!, currentWorkspace!);
            } else {
                modalOnSuccessCallbackRef.current = onSuccess;
                setIsLoginModalOpen(true);
            }
        },
        [authenticatedWorkspace, user, currentWorkspace]
    );

    const closeLoginModal = useCallback(() => {
        setIsLoginModalOpen(false);
    }, []);

    // Guest auth functions
    const getGuestUid = useCallback((): string => {
        // Try to get the analytics anonymous ID from Segment
        const analyticsAnonymousId = (window as any)?.analytics?.user?.()?.anonymousId?.();

        if (analyticsAnonymousId) {
            return analyticsAnonymousId;
        }

        // Fallback: generate a temporary UUID
        // Note: In production, Segment should always be available
        return `guest_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    }, []);

    const initializeGuestAuth = useCallback(async () => {
        // Skip if already initialized
        if (guestAuthInitialized) {
            return;
        }

        // Skip if user is authenticated
        if (user) {
            // console.log("[guest-auth] User is authenticated, skipping guest auth");
            setGuestAuthInitialized(true);
            return;
        }

        try {
            // console.log("[guest-auth] Initializing guest auth");
            const uid = getGuestUid();
            setGuestUid(uid);

            // console.log("[guest-auth] Using guest UID:", uid);

            // Set refresh token (long-lived, 1 week)
            await api.guestAuth.setGuestRefreshToken({ uid, referer: window.location.href });

            // Set access token (short-lived, 15 minutes)
            await api.guestAuth.setGuestAccessToken();

            // console.log("[guest-auth] Guest auth initialized successfully");
            setGuestAuthInitialized(true);
        } catch (error) {
            console.error("[guest-auth] Failed to initialize guest auth:", error);
            // Don't block if guest auth fails, just mark as initialized
            setGuestAuthInitialized(true);
        }
    }, [guestAuthInitialized, user, getGuestUid, api]);

    // Helper functions for common permission checks
    const isAdmin = Boolean(claims?.admin);

    const hasWorkspaceAccess = (workspaceId: string): boolean => {
        if (!claims?.workspaces) return false;
        return Boolean(claims.workspaces[workspaceId]);
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                initialLoadComplete,
                claims,
                apiAuthenticated,
                activeSession,
                authenticatedWorkspace,
                loading,
                setLoading,
                error,
                setError,
                logout,
                loginWithEmail,
                signUpWithEmail,
                sendLoginLink,
                completeLoginWithEmailLink,
                completeLoginWithEmailLinkAndName,
                loginWithGoogle,
                loginWithGithub,
                emailForSignIn,
                setEmailForSignIn,
                emailLinkSent,
                pendingAuthInfo,
                isAdmin,
                hasWorkspaceAccess,
                isLoginModalOpen,
                requireLogin,
                closeLoginModal,
                guestUid,
                guestAuthInitialized,
                initializeGuestAuth
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}
